import { CreatorTypeS } from '../../../../features/Creator/outbound/share';
import { CustomEventWithDetail, html, on, View } from 'rune-ts';
import klass from './CreatorRankingItem.module.scss';
import { htmlIf } from '../../../../shared/util';
import { font, staticFont } from '../../../../shared/typography';
import { makeDefaultProfileImg } from '../../../../shared/util/helper';
import { getResizedUrl } from '../../../../shared/util/image';
import { map, pipe, take, toArray } from '@fxts/core';
import { MShopDefaultIcon } from '../../atoms/Icon';
import { LegacyProductThumbnail } from '../ProductThumbnail/LegacyProductThumbnail.ts';
import { ProductCardView } from '../ProductCard/ProductCardView.ts';
import { ProductCardViewData, ProductCardSize } from '../../../../features/ProductList/type';
import { ChevronDownIcon, ChevronUpIcon } from '../../../../shared/components/atoms/Icon/foundation_icons';
import { makeSubScreenNavigate } from '../../../../shared/app/navigate';
import { ProductListTypeS } from '../../../../features/ProductList/outbound/share';
import { dataStr } from '../../../../shared/util/dataStr';
import { RankDiff } from '@services/MarppleShop/renderApp/components/atoms/RankDiff/RankDiff.ts';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';

export interface CreatorRankingItemProps {
  active: boolean;
  creator: CreatorTypeS.ICreatorRanking;
  default_product_card_props: Partial<ProductCardViewData>;
  is_mobile: boolean;
  store_id: number;
}

class CreatorRankingItemProduct extends LegacyProductThumbnail {
  static wrapWithLink(
    data: ProductListTypeS.ProductThumbnailData,
    option: ProductListTypeS.ProductThumbnailOption,
    domain_name: string,
    product_id: number,
  ) {
    const view = new CreatorRankingItemProduct(data, option);
    const product_detail_url = `/${domain_name}/products/${product_id}`;
    const navigate = makeSubScreenNavigate(product_detail_url);

    return html`
      <a
        class="${klass.product_image_link}"
        href="${product_detail_url}"
        data-post-message="${dataStr(navigate)}"
      >
        ${view}
      </a>
    `;
  }
}

class CreatorRankinItemChangeActiveAfterEvent extends CustomEventWithDetail<CreatorRankingItemProps> {}

export class CreatorRankingItem extends View<CreatorRankingItemProps> {
  public static MAX_PRODUCT_IMAGE_PC_COUNT = 5;
  public static MAX_PRODUCT_IMAGE_MO_COUNT = 4;
  public static MAX_PRODUCT_CARD_COUNT = 5;

  private _productCardViews = this.makeProductCardViews();

  private fanLetterButtonView = new ButtonAction({
    type: 'white',
    label: this.texts.fan_letter,
    size: this.isMobile ? 'regular' : 'large',
    href: this.fanLetterUrl,
    navigate: this._makeNavigate(this.fanLetterUrl),
    klass: klass.button,
  });

  static ChangeActiveAfterEvent = CreatorRankinItemChangeActiveAfterEvent;

  protected override onMount() {
    const params = new URLSearchParams(window.location.search);
    const store_id_from_query = params.get('storeId');

    if (String(this.data.store_id) === store_id_from_query) {
      this.element().scrollIntoView({ behavior: 'smooth' });
    }
  }

  protected override template(data: CreatorRankingItemProps) {
    return html`
      <li class="${klass.creator_raking_item}" data-active="${this.isActive}">
        <div class="${klass.content}">
          <!--    랭크 정보      -->
          <div class="${klass.rank}">
            <span class="${klass.rank_num} ${staticFont('bebas_32_bold', '14_semibold_kr')}">
              ${data.creator.current_rank}
            </span>
            ${new RankDiff({
              ranking: this.data.creator,
              font: staticFont('bebas_20_bold', '10_medium_kr'),
              klass: klass.rank_diff,
            })}
          </div>

          <!--    프로필 이미지      -->
          ${this.profileImageHtml}

          <!--    프로필 정보      -->
          ${this.profileContentHtml}

          <!--    상품 이미지들      -->
          ${this.makeProductImages(!this.isMobile)}

          <!--    접힘 화살표      -->
          ${this.arrowHtml}
        </div>

        <!--    active 상태에서 보일 상품들    -->
        ${htmlIf(
          html`<div class="${klass.active_products}">
            ${htmlIf(html`${this._productCardViews}`, !this.isMobile)}
            ${this.makeProductImages(this.isMobile)}
          </div>`,
          this.data.creator.products.length > 0,
        )}

        <!--    products 가 없을때 보이는 화면    -->
        ${htmlIf(this.makeEmptyHtml(), this.data.creator.products.length === 0)}
      </li>
    `;
  }

  private makeProductImages(condition: boolean) {
    const ProductThumbnailViews = pipe(
      this.data.creator.products,
      map((product) =>
        CreatorRankingItemProduct.wrapWithLink(
          product,
          {
            resize_width: 80 * 2,
            klass: klass.product_image,
          },
          this.data.creator.domain_name,
          product.id,
        ),
      ),
      take(
        this.isMobile
          ? CreatorRankingItem.MAX_PRODUCT_IMAGE_MO_COUNT
          : CreatorRankingItem.MAX_PRODUCT_IMAGE_PC_COUNT,
      ),
      toArray,
    );

    return htmlIf(html`<div class="${klass.product_images}">${ProductThumbnailViews}</div>`, condition);
  }

  private makeProductCardViews() {
    return pipe(
      this.data.creator.products,
      map(
        (product) =>
          new ProductCardView(
            { ...this.data.default_product_card_props, ...product },
            {
              resize_width: 216 * 2,
              size: ProductCardSize.large,
            },
          ),
      ),
      take(CreatorRankingItem.MAX_PRODUCT_CARD_COUNT),
      toArray,
    );
  }

  private makeEmptyHtml() {
    const { texts } = this;
    return html`
      <div class="${klass.empty_products}">
        <div class="${klass.title_wrapper}">
          <div class="${klass.title} ${font('20_bold', '16_bold')}">${texts.title}</div>
          <div class="${klass.sub_title} ${font('16_medium', '14_medium')}">${texts.sub_title}</div>
        </div>
        <div class="${klass.button}">${this.fanLetterButtonView}</div>
      </div>
    `;
  }

  get profileImageHtml() {
    const { creator } = this.data;

    const profile_img = creator.thumbnail_img
      ? getResizedUrl({
          url: creator.thumbnail_img,
          format: 'webp',
          width: 80 * 2,
          quality: 80,
        })
      : makeDefaultProfileImg(300);

    return html`
      <div class="${klass.profile_image}">
        <a href="${this.storeUrl}" data-post-message="${dataStr(this._makeNavigate(this.storeUrl))}">
          <img src="${profile_img}" alt="${html.preventEscape(creator.name || '')}" />
        </a>
      </div>
    `;
  }

  get profileContentHtml() {
    const { creator, is_mobile } = this.data;
    return html`
      <div class="${klass.profile_content}">
        <div class="${font('20_medium', '14_semibold')} ${klass.creator_name}">
          <a href="${this.storeUrl}" data-post-message="${dataStr(this._makeNavigate(this.storeUrl))}">
            <span>${html.preventEscape(creator.name || '')}</span>
          </a>

          ${is_mobile
            ? null
            : html`<a
                href="${this.storeUrl}"
                data-post-message="${dataStr(this._makeNavigate(this.storeUrl))}"
              >
                ${MShopDefaultIcon()}
              </a>`}
        </div>

        ${htmlIf(
          html`
            <a href="${this.storeUrl}" data-post-message="${dataStr(this._makeNavigate(this.storeUrl))}">
              <p class="${font('16_medium', '12_medium')} ${klass.seller_type}">
                ${creator.seller_type_name}
              </p>
            </a>
          `,
          !!creator.seller_type_name,
        )}
      </div>
    `;
  }

  get arrowHtml() {
    return html`
      <div class="${klass.arrow}">
        ${ChevronUpIcon({ klass: klass.up })} ${ChevronDownIcon({ klass: klass.down })}
      </div>
    `;
  }

  get storeUrl() {
    return `/${ET.lang}/${this.data.creator.domain_name}`;
  }

  private get fanLetterUrl() {
    return `/${ET.lang}/${this.data.creator.domain_name}/fan-letter`;
  }

  private _makeNavigate(url: string) {
    return makeSubScreenNavigate(url);
  }

  get isMobile(): boolean {
    return this.data.is_mobile;
  }

  /**
   * 상품 있을때만 활성화 가능
   */
  get isActive(): boolean {
    return this.data.active;
  }

  @on('click')
  toggleActive(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (
      target.closest(`.${klass.creator_name} > a`) ||
      target.closest(`.${klass.product_image}`) ||
      target.closest(`.${klass.profile_image}`) ||
      target.closest(`.${klass.button}`) ||
      target.closest(`.${klass.active_products}`)
    ) {
      return;
    }

    this.data.active = !this.data.active;
    this.element().dataset.active = `${this.data.active}`;
    this.dispatchEvent(CreatorRankingItem.ChangeActiveAfterEvent, { bubbles: true, detail: this.data });
  }

  get texts() {
    return {
      title: ET('mps2::main::creator_ranking::item::title'),
      sub_title: ET('mps2::main::creator_ranking::item::sub_title'),
      fan_letter: ET('mps2::main::creator_ranking::item::fan_letter'),
    };
  }
}
